import { localize } from "@/i18n/translate";
import { useCallback, useEffect, useState } from "react";
import { Check, Copy, KeyRound, Search, X as XIcon } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Link, useSearchParams } from "react-router-dom";
import { api, errorCode } from "@/lib/api";
import { MEMBERSHIP_VALUES, usePortal } from "../i18n";
import { TicketRow } from "./ClientDashboard";
import { ApproveButtons } from "./AdminDashboard";
import { ClientNotes } from "./ClientNotes";
import { Badge, ErrorNote, PageTitle, Spinner } from "../ui";
const Filters = ({
  options,
  value,
  onChange,
  testPrefix
}) => <div role="group" className="flex flex-wrap gap-2">
    {localize(options.map(([v, label]) => <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)} data-testid={`${testPrefix}-${v || "all"}`} className={`px-4 py-2 text-sm font-medium transition-colors ${value === v ? "bg-ink text-paper" : "border border-ink/20 hover:border-ink"}`}>
        {localize(label)}
      </button>))}
  </div>;
const useSearchTerm = (params, setParams) => {
  const q = params.get("q") ?? "";
  const [term, setTerm] = useState(q);
  useEffect(() => {
    if (term.trim() === q) return undefined;
    const t = setTimeout(() => {
      const n = new URLSearchParams(params);
      if (term.trim()) n.set("q", term.trim());else n.delete("q");
      setParams(n, {
        replace: true
      });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);
  return {
    q,
    term,
    setTerm
  };
};
const SearchBox = ({
  id,
  label,
  placeholder,
  term,
  setTerm,
  testPrefix
}) => {
  const {
    p
  } = usePortal();
  return <form role="search" onSubmit={e => e.preventDefault()} className="relative max-w-xl">
      <label htmlFor={id} className="sr-only">{localize(label)}</label>
      <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-mute" aria-hidden="true" />
      <input id={id} type="search" value={term} onChange={e => setTerm(e.target.value)} placeholder={localize(placeholder)} data-testid={`${testPrefix}-input`} className="block w-full border border-ink/25 bg-white/70 py-3 pl-11 pr-11 text-base text-ink placeholder:text-zinc-500 focus:border-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-signal [&::-webkit-search-cancel-button]:hidden" />
      {localize(term && <button type="button" onClick={() => setTerm("")} aria-label={localize(p.adm.clearSearch)} data-testid={`${testPrefix}-clear`} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-mute hover:text-signal">
          <XIcon className="h-4 w-4" aria-hidden="true" />
        </button>)}
    </form>;
};
export function AdminTickets() {
  const {
    p,
    errText
  } = usePortal();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "offen";
  const userId = params.get("user");
  const {
    q,
    term,
    setTerm
  } = useSearchTerm(params, setParams);
  const [tickets, setTickets] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setTickets(null);
    const query = new URLSearchParams();
    if (status) query.set("status", status);
    if (userId) query.set("user_id", userId);
    if (q) query.set("q", q);
    api.get(`/admin/tickets?${query}`).then(r => setTickets(Array.isArray(r.data) ? r.data : [])).catch(e => {
      setErr(errText(errorCode(e)));
      setTickets([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, userId, q]);
  const setStatus = s => {
    const n = new URLSearchParams(params);
    n.set("status", s);
    setParams(n);
  };
  return <div data-testid="admin-tickets-page">
      <PageTitle eyebrow={p.admin} title={localize(p.nav.tickets)} />
      <div className="mt-8 flex flex-col gap-4">
        <SearchBox id="ticket-search" label={p.adm.search} placeholder={localize(p.adm.searchPh)} term={term} setTerm={setTerm} testPrefix="ticket-search" />
        <Filters testPrefix="ticket-filter" value={status} onChange={setStatus} options={[["offen", p.adm.openF], ["neu", p.ticket.neu], ["in_bearbeitung", p.ticket.in_bearbeitung], ["wartet_auf_kunde", p.ticket.wartet_auf_kunde], ["abgeschlossen", p.ticket.abgeschlossen], ["", p.adm.all]]} />
        {localize(userId && <p className="text-sm text-mute">{localize(p.adm.filterClient)} · <Link to={`/admin/anfragen?status=${status}`} className="text-ink underline underline-offset-4 hover:text-signal">{localize(p.adm.clearFilter)}</Link></p>)}
      </div>
      <ErrorNote>{localize(err)}</ErrorNote>
      {localize(tickets === null ? <Spinner label={p.loading} /> : tickets.length === 0 ? <p className="mt-8 text-mute" data-testid="admin-tickets-empty">{localize(q ? p.adm.noResults(q) : p.adm.noTickets)}</p> : <>
        {localize(q && <p className="mt-6 text-sm text-mute" role="status" data-testid="ticket-search-count">{localize(p.adm.results(tickets.length))}</p>)}
        <ul className="mt-6 border-t border-ink/15" data-testid="admin-tickets-list">
          {localize(tickets.map(t => <TicketRow key={t.id} t={t} to={`/admin/anfragen/${t.id}`} showClient />))}
        </ul>
        </>)}
    </div>;
}
const MembershipSelect = ({
  user,
  onSaved
}) => {
  const {
    p,
    membershipLabel,
    errText
  } = usePortal();
  const [err, setErr] = useState(null);
  const change = async e => {
    setErr(null);
    try {
      await api.patch(`/admin/users/${user.id}`, {
        membership: e.target.value
      });
      onSaved?.();
    } catch (ex) {
      setErr(errText(errorCode(ex)));
    }
  };
  return <div>
      <label htmlFor={`m-${user.id}`} className="eyebrow">{localize(p.adm.membership)}</label>
      <select id={`m-${user.id}`} value={user.membership || ""} onChange={change} className="mt-1 block w-full max-w-[260px] border border-ink/25 bg-white px-3 py-2 text-sm" data-testid={`membership-select-${user.email}`}>
        <option value="">{localize(p.adm.none)}</option>
        {localize(MEMBERSHIP_VALUES.map(m => <option key={m} value={m}>{localize(membershipLabel(m))}</option>))}
      </select>
      {localize(err && <p className="mt-1 text-xs text-signaldark">{localize(err)}</p>)}
    </div>;
};
const TempPassword = ({
  user
}) => {
  const {
    p,
    errText
  } = usePortal();
  const t = p.temp;
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(null);
  const create = async e => {
    e.preventDefault();
    setErr(null);
    try {
      const {
        data
      } = await api.post(`/admin/users/${user.id}/temp-password`);
      setPw(data?.temporary_password ?? null);
    } catch (ex) {
      setErr(errText(errorCode(ex)));
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pw);
      setCopied(true);
    } catch (ex) {
      setCopied(false);
    }
  };
  const onOpenChange = o => {
    setOpen(o);
    if (!o) {
      setPw(null);
      setCopied(false);
      setErr(null);
    }
  };
  return <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1.5 border border-ink/30 px-3 py-2 text-sm font-medium hover:border-ink" data-testid={`temp-password-${user.email}`}>
          <KeyRound className="h-4 w-4" aria-hidden="true" /> {localize(t.button)}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-none bg-paper" data-lenis-prevent data-testid="temp-password-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">{localize(pw ? t.resultTitle : t.confirmTitle)}</AlertDialogTitle>
          <AlertDialogDescription className="text-mute">{localize(pw ? t.resultText : t.confirmText(user.name))}</AlertDialogDescription>
        </AlertDialogHeader>
        {localize(pw && <div className="flex items-center gap-3 border border-ink/20 bg-white p-4">
            <code className="flex-1 select-all break-all font-mono text-lg tracking-wider" data-testid="temp-password-value">{localize(pw)}</code>
            <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 border border-ink/30 px-3 py-2 text-sm hover:border-ink" data-testid="temp-password-copy">
              {localize(copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />)} {localize(copied ? t.copied : t.copy)}
            </button>
          </div>)}
        {localize(err && <p role="alert" className="text-sm text-signaldark">{localize(err)}</p>)}
        <AlertDialogFooter>
          {localize(pw ? <AlertDialogCancel className="rounded-none" data-testid="temp-password-close">{localize(t.close)}</AlertDialogCancel> : <>
              <AlertDialogCancel className="rounded-none">{localize(t.cancel)}</AlertDialogCancel>
              <AlertDialogAction onClick={create} className="rounded-none bg-signal hover:bg-signaldark" data-testid="temp-password-confirm">{localize(t.confirm)}</AlertDialogAction>
            </>)}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>;
};
export function AdminClients() {
  const {
    p,
    fmtDate,
    errText
  } = usePortal();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "approved";
  const {
    q,
    term,
    setTerm
  } = useSearchTerm(params, setParams);
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    const query = new URLSearchParams();
    if (status) query.set("status", status);
    if (q) query.set("q", q);
    api.get(`/admin/users?${query}`).then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(e => {
      setErr(errText(errorCode(e)));
      setUsers([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q]);
  useEffect(() => {
    setUsers(null);
    load();
  }, [load]);
  return <div data-testid="admin-clients-page">
      <PageTitle eyebrow={p.admin} title={localize(p.nav.clients)} />
      <div className="mt-8 flex flex-col gap-4">
        <SearchBox id="client-search" label={p.adm.searchClients} placeholder={localize(p.adm.searchClientsPh)} term={term} setTerm={setTerm} testPrefix="client-search" />
        <Filters testPrefix="client-filter" value={status} onChange={s => setParams(q ? {
        status: s,
        q
      } : {
        status: s
      })} options={[["approved", p.userStatus.approved], ["rejected", p.userStatus.rejected], ["", p.adm.all]]} />
      </div>
      <ErrorNote>{localize(err)}</ErrorNote>
      {localize(users === null ? <Spinner label={p.loading} /> : users.length === 0 ? <p className="mt-8 text-mute" data-testid="admin-clients-empty">{localize(q ? p.adm.noClientResults(q) : p.adm.noClients)}</p> : <>
        {localize(q && <p className="mt-6 text-sm text-mute" role="status" data-testid="client-search-count">{localize(p.adm.results(users.length))}</p>)}
        <ul className="mt-6 border-t border-ink/15" data-testid="admin-clients-list">
          {localize(users.map(u => <li key={u.id} data-testid={`client-row-${u.email}`} className="grid gap-5 border-b border-ink/15 py-6">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="font-display text-lg font-bold">{localize(u.name)}</p>
                  <Badge kind="user" value={u.status} label={p.userStatus[u.status]} />
                </div>
                <p className="mt-1 break-all text-sm text-mute">
                  {localize(u.organisation && `${u.organisation} · `)}<a href={`mailto:${u.email}`} className="text-ink hover:text-signal">{localize(u.email)}</a>{localize(u.phone && ` · ${u.phone}`)}
                </p>
                <p className="mt-1 text-xs text-mute">{localize(p.adm.registered)} {localize(fmtDate(u.created_at))}</p>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                {localize(u.status === "approved" && <MembershipSelect user={u} onSaved={load} />)}
                <ApproveButtons user={u} onDone={load} />
                {localize(u.status === "approved" && <TempPassword user={u} />)}
                {localize(u.status === "approved" && <Link to={`/admin/anfragen?status=&user=${u.id}`} className="text-sm font-medium underline underline-offset-4 hover:text-signal">{localize(p.adm.ticketsOf)}</Link>)}
              </div>
              <ClientNotes key={`${u.id}-${u.notes_count}`} user={u} />
            </li>))}
        </ul>
        </>)}
    </div>;
}
export function AdminContacts() {
  const {
    p,
    fmtDate,
    errText
  } = usePortal();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/contact-requests").then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(e => {
      setErr(errText(errorCode(e)));
      setItems([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const toggle = async ref => {
    try {
      await api.patch(`/admin/contact-requests/${ref}`);
      load();
    } catch (e) {
      setErr(errText(errorCode(e)));
    }
  };
  return <div data-testid="admin-contacts-page">
      <PageTitle eyebrow={p.admin} title={localize(p.nav.contacts)} />
      <ErrorNote>{localize(err)}</ErrorNote>
      {localize(items === null ? <Spinner label={p.loading} /> : items.length === 0 ? <p className="mt-8 text-mute" data-testid="admin-contacts-empty">{localize(p.adm.noContacts)}</p> : <ul className="mt-6 space-y-4" data-testid="admin-contacts-list">
          {localize(items.map(c => <li key={c.ref} className={`border p-6 ${c.done ? "border-ink/10 bg-paper2/50" : "border-ink/15 bg-white/60"}`} data-testid={`contact-request-${c.ref}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-xs text-mute">{localize(p.dash.ref)} {localize(c.ref)}</span>
                  <span className="border border-ink/20 px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.12em]">{localize(p.categories[c.topic] || c.topic)}</span>
                  {localize(c.done && <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-mute">{localize(p.adm.done)}</span>)}
                </div>
                <span className="font-mono text-xs text-mute">{localize(fmtDate(c.created_at))}</span>
              </div>
              <p className="mt-3 font-semibold">{localize(c.name)}{localize(c.organisation && <span className="font-normal text-mute"> · {localize(c.organisation)}</span>)}</p>
              {c.phone && <p className="mt-2 text-sm"><a href={`tel:${c.phone.replace(/[^+\d]/g, "")}`} className="hover:text-signal">{c.phone}</a></p>}
              <p className="mt-3 whitespace-pre-wrap break-words leading-relaxed">{localize(c.message)}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={`mailto:${c.email}?subject=${encodeURIComponent(`Ihre Anfrage – Ref. ${c.ref}`)}`} className="inline-flex items-center gap-2 bg-ink px-4 py-2 text-sm font-medium text-paper hover:bg-signal" data-testid={`contact-reply-${c.ref}`}>{localize(p.adm.reply)}: {localize(c.email)}</a>
                <button type="button" onClick={() => toggle(c.ref)} className="border border-ink/25 px-4 py-2 text-sm font-medium hover:border-ink" data-testid={`contact-toggle-${c.ref}`}>{localize(c.done ? p.adm.markOpen : p.adm.markDone)}</button>
              </div>
            </li>))}
        </ul>)}
    </div>;
}
